import type { EntitlementAccess } from "./entitlement";

export const FEATURES = {
  authenticate: "authenticate",
  dashboardLocal: "dashboard.local",
  importParse: "import.parse",
  importMap: "import.map",
  importValidate: "import.validate",
  importPreview: "import.preview",
  previewReports: "reports.preview",
  basicReports: "reports.basic",
  localExport: "export.local",
  cloudRead: "cloud.read",
  cloudSync: "cloud.sync",
  caseCreate: "case.create",
  caseEdit: "case.edit",
  caseDelete: "case.delete",
  caseArchive: "case.archive",
  importCommit: "import.commit",
  bulkEdit: "case.bulk-edit",
  oneClickWrite: "one-click.write",
  advancedReports: "reports.advanced",
  savedReports: "reports.saved",
} as const;

export type Feature = (typeof FEATURES)[keyof typeof FEATURES];

const freeFeatures = new Set<Feature>([
  FEATURES.authenticate,
  FEATURES.dashboardLocal,
]);

const lapsedFeatures = new Set<Feature>([
  ...freeFeatures,
  FEATURES.previewReports,
  FEATURES.basicReports,
  FEATURES.localExport,
  FEATURES.cloudRead,
  FEATURES.advancedReports,
]);

const proFeatures = new Set<Feature>([
  ...freeFeatures,
  FEATURES.cloudRead,
  FEATURES.cloudSync,
  FEATURES.caseCreate,
  FEATURES.caseEdit,
  FEATURES.caseDelete,
  FEATURES.caseArchive,
  FEATURES.importCommit,
  FEATURES.bulkEdit,
  FEATURES.oneClickWrite,
  FEATURES.advancedReports,
  FEATURES.savedReports,
]);

export interface AccessContext {
  entitlement: EntitlementAccess;
  authenticated: boolean;
}

export interface FeatureDecision {
  allowed: boolean;
  reason?: "authentication_required" | "pro_required";
}

export function decideFeature(feature: Feature, context: AccessContext): FeatureDecision {
  if (freeFeatures.has(feature)) return { allowed: true };
  if (!context.authenticated) return { allowed: false, reason: "authentication_required" };
  if (context.entitlement === "lapsed") {
    return lapsedFeatures.has(feature)
      ? { allowed: true }
      : { allowed: false, reason: "pro_required" };
  }
  if (context.entitlement !== "pro") return { allowed: false, reason: "pro_required" };
  return { allowed: proFeatures.has(feature) };
}

export function canUse(feature: Feature, context: AccessContext): boolean {
  return decideFeature(feature, context).allowed;
}
